// src/hooks/useOnboarding.js

import { useState, useEffect, useCallback, useRef } from 'react';
import { doc, onSnapshot, setDoc, serverTimestamp, deleteField } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';

function normalizeHintsSeen(raw) {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw;
  }
  return {};
}

function hintsSeenEqual(a, b) {
  const left = a || {};
  const right = b || {};
  const keysA = Object.keys(left);
  const keysB = Object.keys(right);
  if (keysA.length !== keysB.length) return false;
  return keysA.every((key) => left[key] === right[key]);
}

function completedAtEqual(a, b) {
  if (a === b) return true;
  if (a == null || b == null) return false;
  const msA = typeof a?.toMillis === 'function' ? a.toMillis() : a;
  const msB = typeof b?.toMillis === 'function' ? b.toMillis() : b;
  return msA === msB;
}

export function useOnboarding() {
  const { user } = useAuth();
  const uid = user?.uid ?? null;
  const [loading, setLoading] = useState(() => Boolean(uid));
  const [onboardingCompletedAt, setOnboardingCompletedAt] = useState(null);
  const [onboardingEnabled, setOnboardingEnabledState] = useState(true);
  const [onboardingHintsSeen, setOnboardingHintsSeen] = useState({});
  const [welcomeDismissedOptimistic, setWelcomeDismissedOptimistic] = useState(false);
  const completingWelcomeRef = useRef(false);

  useEffect(() => {
    setWelcomeDismissedOptimistic(false);
    completingWelcomeRef.current = false;
  }, [uid]);

  useEffect(() => {
    if (!uid) {
      setLoading(false);
      setOnboardingCompletedAt(null);
      setOnboardingEnabledState(true);
      setOnboardingHintsSeen({});
      return undefined;
    }

    const settingsRef = doc(db, 'users', uid, 'settings', 'preferences');
    let cancelled = false;
    setLoading(true);

    const unsub = onSnapshot(
      settingsRef,
      (snap) => {
        if (cancelled) return;

        if (!snap.exists()) {
          setOnboardingCompletedAt((prev) => (prev == null ? prev : null));
          setOnboardingEnabledState((prev) => (prev === true ? prev : true));
          setOnboardingHintsSeen((prev) => (hintsSeenEqual(prev, {}) ? prev : {}));
          setLoading(false);
          completingWelcomeRef.current = false;
          return;
        }

        const data = snap.data();
        const nextCompleted = data.onboardingCompletedAt ?? null;
        const nextEnabled = data.onboardingEnabled !== false;
        const nextHints = normalizeHintsSeen(data.onboardingHintsSeen);

        setOnboardingCompletedAt((prev) =>
          completedAtEqual(prev, nextCompleted) ? prev : nextCompleted,
        );
        setOnboardingEnabledState((prev) => (prev === nextEnabled ? prev : nextEnabled));
        setOnboardingHintsSeen((prev) => {
          const merged = { ...nextHints };
          for (const key of Object.keys(prev)) {
            if (prev[key]) merged[key] = true;
          }
          return hintsSeenEqual(prev, merged) ? prev : merged;
        });
        setLoading(false);
        completingWelcomeRef.current = false;
      },
      () => {
        if (!cancelled) setLoading(false);
      },
    );

    return () => {
      cancelled = true;
      unsub();
    };
  }, [uid]);

  const welcomeCompleted = onboardingCompletedAt != null || welcomeDismissedOptimistic;

  const shouldShowHint = useCallback(
    (hintId, { prerequisiteHintId } = {}) => {
      if (loading || !welcomeCompleted || onboardingEnabled === false) return false;
      if (onboardingHintsSeen[hintId]) return false;
      if (prerequisiteHintId && !onboardingHintsSeen[prerequisiteHintId]) return false;
      return true;
    },
    [loading, welcomeCompleted, onboardingEnabled, onboardingHintsSeen],
  );

  const completeWelcome = useCallback(async () => {
    if (!uid || completingWelcomeRef.current) return;
    completingWelcomeRef.current = true;
    setWelcomeDismissedOptimistic(true);
    try {
      const settingsRef = doc(db, 'users', uid, 'settings', 'preferences');
      await setDoc(
        settingsRef,
        {
          userId: uid,
          onboardingCompletedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
    } catch (err) {
      completingWelcomeRef.current = false;
      setWelcomeDismissedOptimistic(false);
      throw err;
    }
  }, [uid]);

  const dismissHint = useCallback(
    async (hintId) => {
      if (!uid) return;
      setOnboardingHintsSeen((prev) => {
        if (prev[hintId]) return prev;
        return { ...prev, [hintId]: true };
      });
      const settingsRef = doc(db, 'users', uid, 'settings', 'preferences');
      await setDoc(
        settingsRef,
        {
          userId: uid,
          [`onboardingHintsSeen.${hintId}`]: true,
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
    },
    [uid],
  );

  const setOnboardingEnabled = useCallback(
    async (enabled) => {
      if (!uid) return;
      setOnboardingEnabledState((prev) => (prev === enabled ? prev : enabled));
      const settingsRef = doc(db, 'users', uid, 'settings', 'preferences');
      try {
        await setDoc(
          settingsRef,
          {
            userId: uid,
            onboardingEnabled: enabled,
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        );
      } catch (err) {
        setOnboardingEnabledState((prev) => (prev === enabled ? !enabled : prev));
        throw err;
      }
    },
    [uid],
  );

  const restartOnboarding = useCallback(async () => {
    if (!uid) return;
    setWelcomeDismissedOptimistic(false);
    completingWelcomeRef.current = false;
    setOnboardingCompletedAt(null);
    setOnboardingHintsSeen({});
    setOnboardingEnabledState(true);
    const settingsRef = doc(db, 'users', uid, 'settings', 'preferences');
    await setDoc(
      settingsRef,
      {
        userId: uid,
        onboardingCompletedAt: deleteField(),
        onboardingHintsSeen: deleteField(),
        onboardingEnabled: true,
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
  }, [uid]);

  return {
    loading,
    welcomeCompleted,
    onboardingEnabled,
    onboardingHintsSeen,
    shouldShowHint,
    completeWelcome,
    dismissHint,
    setOnboardingEnabled,
    restartOnboarding,
  };
}
